import React, { useEffect, useState } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';

import { useQueryClient } from '@tanstack/react-query';
import { useFormik } from 'formik';

import { iaRa, optionify } from 'lib/helpers/utility';
import { useBillingProvider } from 'lib/hooks/queries/practice/useBillingProvider';
import { useStates } from 'lib/hooks/queries/states/useStates';

import { showAlert } from 'components/shared/Alert/Alert';
import Button from 'components/shared/Buttons/Button';
import Allowed from 'components/shared/Permissions/Allowed';

import NewBillingProvider from './NewBillingProvider';
import Row from './Row';
import { billingProviders } from './lib/billingProviders';
import getInitialValues from './lib/getInitialValues';
import PracticeBillingValidationSchema from './lib/practiceBillingValidationSchema';

export default function AdminBilling() {
  const navigate = useNavigate();

  const queryClient = useQueryClient();
  const { data: statesData } = useStates();
  const states = optionify(statesData?.states);
  const { practice } = useOutletContext();
  const [showModal, setShowModal] = useState(false);
  const [modalType, setModalType] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const { submitBillingProvider, handleDeletion } = billingProviders({
    queryClient,
    navigate,
    setShowModal,
    setIsDeleting
  });

  const { data, isLoading, isFetching } = useBillingProvider({
    params: { practiceId: practice?.id },
    dependencies: [practice?.id]
  });

  const billingInfo = !isLoading && !isFetching && data ? data.billInfo : undefined;

  const openModal = (type, initialValues) => {
    formik.resetForm();
    formik.setValues(initialValues || formik.initialValues);
    setShowModal(true);
    setModalType(type);
  };

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: getInitialValues(practice),
    validationSchema: PracticeBillingValidationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);

      const hasNpiAndTaxId = values.bill_npi && values.bill_taxid;
      const hasTaxonomyAndId = values.bill_taxonomy && values.bill_id;

      if (!hasNpiAndTaxId && !hasTaxonomyAndId) {
        showAlert({
          title: 'Please provide either NPI & Tax ID or Taxonomy & ID.',
          color: 'danger'
        });
        setSubmitting(false);
        return;
      }

      setShowModal(false);
      await submitBillingProvider(values, { setSubmitting });
      setSubmitting(false);
    }
  });

  useEffect(() => {
    if (!practice?.id) return;
  }, [practice?.id]);

  const selectBiller = (biller) => {
    const updatedValues = {
      id: biller.id,
      practice_id: biller.practice_id,
      bill_name: biller.bill_name,
      bill_npi: biller.bill_npi,
      bill_taxid: biller.bill_taxid,
      bill_taxid_type: biller.bill_taxid_type,
      bill_taxonomy: biller.bill_taxonomy,
      bill_id: biller.bill_id,
      bill_addr_1: biller.bill_addr_1,
      bill_addr_2: biller.bill_addr_2,
      bill_city: biller.bill_city,
      bill_state: biller.bill_state,
      bill_zip: biller.bill_zip,
      bill_phone: biller.bill_phone,
      practitioner_ids: biller.practitioner_ids
    };
    openModal('update', updatedValues);
  };

  return (
    <Allowed requiredPermissions="billing.read" showMessage showIllustration>
      <div className="flex h-fit min-h-[30vh] flex-col p-[1rem]">
        <Allowed requiredPermissions="billing.create">
          <div className="flex w-full items-center justify-between">
            <h3 className="w-max text-lg font-500 capitalize leading-7 text-primary-800 md:text-base">
              Billing Providers
            </h3>
            <Button
              icon="add-square-mono"
              size="small"
              transparent
              iconColor="white"
              onClick={() => openModal('create', formik.initialValues)}
              text="New Billing Provider"
              className="w-fit"
            />
          </div>
        </Allowed>
        <div className="!mt-6 grid grid-cols-2 gap-4 overflow-auto !pr-4">
          {iaRa(billingInfo)?.map((row) => {
            return (
              <Row
                key={row.id}
                row={row}
                members={practice?.members}
                onClick={() => selectBiller(row)}
                states={states}
              />
            );
          })}
        </div>
        {showModal && (
          <NewBillingProvider
            formik={formik}
            open={showModal}
            type={modalType}
            handleClose={() => {
              setShowModal(false);
              setModalType(null);
            }}
            isLoading={formik.isSubmitting}
            handleSubmit={formik.submitForm}
            states={states}
            loadedMembers={iaRa(practice?.members).map((member) => {
              return { label: `${member.f_name} ${member.l_name}`, value: member.id };
            })}
            handleDeletion={handleDeletion}
            isDeleting={isDeleting}
          />
        )}
      </div>
    </Allowed>
  );
}
